import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  ChevronDown, 
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Shield,
  Activity,
  Clock,
  MapPin,
  User,
  Monitor,
  Globe,
  Eye,
  ShieldAlert,
  UserCog,
  Network,
  Lock,
  Info,
  HardDrive,
  RefreshCw
} from 'lucide-react';

const LogDashboard = () => {
  const [logs, setLogs] = useState({
    authentication_logs: [],
    user_activity_logs: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('authentication');
  const [isRequesting, setIsRequesting] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateRange: '24h'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 5,
    totalItems: 0,
    pageNumbers: []
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc'
  });
  const [expandedLog, setExpandedLog] = useState(null);

 
  const calculatePageNumbers = (totalItems, itemsPerPage, currentPage) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const pages = [];
    const visiblePages = 3; // Number of pages to show around current page
    
    // Always show first page
    if (totalPages > 0) pages.push(1);
    
    // Determine range of pages to show
    let startPage = Math.max(2, currentPage - Math.floor(visiblePages/2));
    let endPage = Math.min(totalPages - 1, currentPage + Math.floor(visiblePages/2));
    
    // Adjust if we're at the start or end
    if (currentPage <= Math.floor(visiblePages/2) + 1) {
      endPage = Math.min(visiblePages, totalPages - 1);
    }
    if (currentPage >= totalPages - Math.floor(visiblePages/2)) {
      startPage = Math.max(2, totalPages - visiblePages + 1);
    }
    
    // Add pages in range
    for (let i = startPage; i <= endPage; i++) {
      if (i > 1 && i < totalPages) pages.push(i);
    }
    
    // Always show last page if different from first
    if (totalPages > 1) pages.push(totalPages);
    
    // Add ellipsis if gaps exist
    const formattedPages = [];
    let prevPage = 0;
    
    pages.forEach(page => {
      if (page - prevPage > 1) formattedPages.push('...');
      formattedPages.push(page);
      prevPage = page;
    });

    return formattedPages;
  };

  const handlePageChange = (page) => {
    if (page === '...' || page === pagination.currentPage) return;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      pageNumbers: calculatePageNumbers(
        prev.totalItems,
        prev.itemsPerPage,
        page
      )
    }));
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' 
      ? 'desc' 
      : 'asc';
    setSortConfig({ key, direction });
  };

  const toggleLogDetails = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };



const fetchLogs = async () => {
  if (isRequesting) return; // Prevent making a new request if one is already in progress

  try {
    setIsRequesting(true);  // Mark the request as in progress
    setLoading(true);

    const response = await axios.get('/api/logs/all', {
      params: {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: filters.search,
        status: filters.status !== 'all' ? filters.status : undefined,
        dateRange: filters.dateRange,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction
      },
      withCredentials: true
    });

    setLogs(response.data.logs);
    setPagination(prev => ({
      ...prev,
      totalItems: response.data.totalCount,
      pageNumbers: calculatePageNumbers(
        response.data.totalCount, 
        prev.itemsPerPage, 
        prev.currentPage
      )
    }));
  } catch (err) {
    setError(err.response?.data?.message || 'Failed to fetch logs');
  } finally {
    setIsRequesting(false);  // Mark the request as completed
    setLoading(false);
  }
};

useEffect(() => {
  fetchLogs();
}, [pagination.currentPage, filters, sortConfig]);

  

  const renderStatusBadge = (success) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
      success ? 'bg-emerald-900/30 text-emerald-400' : 'bg-rose-900/30 text-rose-400'
    }`}>
      {success ? (
        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
      ) : (
        <XCircle className="mr-1.5 h-3.5 w-3.5" />
      )}
      {success ? 'SUCCESS' : 'FAILED'}
    </span>
  );

  const renderGeoInfo = (log) => (
    <div className="flex items-center space-x-2">
      <MapPin className="h-4 w-4 text-gray-400" />
      <span className="text-sm font-medium text-gray-300">
        {log.city || 'Unknown'}, {log.region || 'Unknown'}, {log.country || 'Unknown'}
      </span>
    </div>
  );

  const renderDeviceInfo = (log) => (
    <div className="flex items-center space-x-3">
      <div className="flex items-center">
        <Monitor className="h-4 w-4 text-gray-400 mr-1.5" />
        <span className="text-sm text-gray-300">
          {log.operating_system || 'Unknown'} {log.operating_system_version || ''}
        </span>
      </div>
      <div className="flex items-center">
        <Globe className="h-4 w-4 text-gray-400 mr-1.5" />
        <span className="text-sm text-gray-300">
          {log.browser || 'Unknown'} {log.browser_version || ''}
        </span>
      </div>
    </div>
  );

  const renderLogDetails = (log) => (
    <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700/50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-xs font-semibold text-gray-400 mb-2">IP ADDRESS</h4>
          <p className="text-sm font-mono text-gray-300">{log.ip_address}</p>
          
          <h4 className="text-xs font-semibold text-gray-400 mb-2 mt-4">LOCATION</h4>
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-sm text-gray-300">
                {log.city || 'Unknown'}, {log.region || 'Unknown'}, {log.country || 'Unknown'}
              </p>
              {log.latitude && log.longitude && (
                <p className="text-xs text-gray-400 mt-1">
                  Coordinates: {log.latitude.toFixed(4)}, {log.longitude.toFixed(4)}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <h4 className="text-xs font-semibold text-gray-400 mb-2">DEVICE INFORMATION</h4>
          <div className="flex items-center space-x-3 mb-3">
            <Monitor className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-300">
              {log.operating_system || 'Unknown'} {log.operating_system_version || ''}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Globe className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-300">
              {log.browser || 'Unknown'} {log.browser_version || ''}
            </span>
          </div>
          
          <h4 className="text-xs font-semibold text-gray-400 mb-2 mt-4">USER AGENT</h4>
          <p className="text-xs font-mono text-gray-400 break-all">{log.user_agent}</p>
        </div>
      </div>
      
      <div className="mt-6">
        <h4 className="text-xs font-semibold text-gray-400 mb-2">
          {activeTab === 'authentication' ? 'AUTHENTICATION MESSAGE' : 'ACTIVITY DESCRIPTION'}
        </h4>
        <p className="text-sm text-gray-300">
          {activeTab === 'authentication' ? log.message : log.description}
        </p>
      </div>
      
      {activeTab === 'activity' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <h4 className="text-xs font-semibold text-gray-400 mb-2">SESSION ID</h4>
            <p className="text-sm font-mono text-gray-300">{log.session_id || 'N/A'}</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-400 mb-2">USER EMAIL</h4>
            <p className="text-sm text-gray-300">{log.email || 'N/A'}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-lg bg-blue-900/20">
                <Shield className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Security Log Monitor</h1>
                <p className="text-sm text-gray-400">Advanced log monitoring dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchLogs}
                className="inline-flex items-center px-3.5 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-sm font-medium transition-colors"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters Card */}
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg mb-6 p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Search */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2">SEARCH LOGS</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-500" />
                </div>
                <input
                  type="text"
                  className="bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 w-full pl-10 pr-3 py-2.5 rounded-md text-sm transition-all"
                  placeholder="Search across all fields..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
            </div>
            
            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2">STATUS</label>
              <select
                className="bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 w-full px-3 py-2.5 rounded-md text-sm transition-all"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="all">All Statuses</option>
                <option value="success">Successful Only</option>
                <option value="failed">Failed Only</option>
              </select>
            </div>
            
            {/* Date Range */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2">DATE RANGE</label>
              <select
                className="bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 w-full px-3 py-2.5 rounded-md text-sm transition-all"
                value={filters.dateRange}
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              >
                <option value="1h">Last 1 hour</option>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="all">All time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700/50 mb-6">
          <button
            onClick={() => setActiveTab('authentication')}
            className={`px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'authentication' 
                ? 'border-blue-500 text-blue-400' 
                : 'border-transparent text-gray-400 hover:text-gray-300'
            } flex items-center`}
          >
            <Shield className="mr-2 h-4 w-4" />
            Authentication Logs
            <span className="ml-2 bg-gray-700 text-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full">
              {logs.authentication_logs?.length || 0}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'activity' 
                ? 'border-blue-500 text-blue-400' 
                : 'border-transparent text-gray-400 hover:text-gray-300'
            } flex items-center`}
          >
            <Activity className="mr-2 h-4 w-4" />
            User Activity
            <span className="ml-2 bg-gray-700 text-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full">
              {logs.user_activity_logs?.length || 0}
            </span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-800/50 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-300">Error fetching logs</h3>
              <p className="text-sm text-red-400/80 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-pulse flex flex-col items-center">
              <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-2" />
              <p className="text-sm text-gray-400">Loading security logs...</p>
            </div>
          </div>
        )}

        {/* Log Tables */}
        {!loading && (
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700/50">
                <thead className="bg-gray-800">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      #
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort(activeTab === 'authentication' ? 'timestamp' : 'created_at')}
                    >
                      <div className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        Timestamp
                        {sortConfig.key === (activeTab === 'authentication' ? 'timestamp' : 'created_at') && (
                          sortConfig.direction === 'asc' 
                            ? <ChevronUp className="ml-1 h-4 w-4" /> 
                            : <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('user_id')}
                    >
                      <div className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        User ID
                        {sortConfig.key === 'user_id' && (
                          sortConfig.direction === 'asc' 
                            ? <ChevronUp className="ml-1 h-4 w-4" /> 
                            : <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    {activeTab === 'authentication' ? (
                      <>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          IP Address
                        </th>
                      </>
                    ) : (
                      <>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Activity Type
                        </th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Email
                        </th>
                      </>
                    )}
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800/30 divide-y divide-gray-700/50">
                  {(activeTab === 'authentication' ? logs.authentication_logs : logs.user_activity_logs)
                    .slice(0, pagination.itemsPerPage)
                    .map((log, index) => (
                      <React.Fragment key={log.id}>
                        <tr className="hover:bg-gray-700/30">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-300">
                            {(pagination.currentPage - 1) * pagination.itemsPerPage + index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {new Date(log.timestamp || log.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {log.user_id}
                          </td>
                          {activeTab === 'authentication' ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {renderStatusBadge(log.success)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                {log.ip_address}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-900/30 text-blue-400">
                                  {log.activity_type}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                {log.email || 'N/A'}
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderGeoInfo(log)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => toggleLogDetails(log.id)}
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                              title="View details"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Details
                            </button>
                          </td>
                        </tr>
                        {expandedLog === log.id && renderLogDetails(log)}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-800 border-t border-gray-700/50 px-6 py-4 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage * pagination.itemsPerPage >= pagination.totalItems}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-400">
                    Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">First</span>
                      <ChevronsLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-700 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronDown className="h-5 w-5 transform rotate-90" />
                    </button>
                    
                    {/* Page numbers */}
                    {pagination.pageNumbers.map((page, index) => (
                      <button
                        key={index}
                        onClick={() => handlePageChange(page)}
                        disabled={page === '...'}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.currentPage
                            ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                            : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                        } ${page === '...' ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        {page === '...' ? '...' : page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage * pagination.itemsPerPage >= pagination.totalItems}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-700 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronDown className="h-5 w-5 transform -rotate-90" />
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.ceil(pagination.totalItems / pagination.itemsPerPage))}
                      disabled={pagination.currentPage * pagination.itemsPerPage >= pagination.totalItems}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronsRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LogDashboard;
